import { useEffect, useState } from 'react';
import { read_cookie } from 'sfcookies';
import { hostUrl, UseUrlManagerCleanUrl } from '../../../../../utils/url-manager';

/** The band one result's health falls in. Mirrors `MiniRiskBand` on the API side. */
export type MiniRiskBand = 'high' | 'medium' | 'low' | 'pending';

/**
 * How one result was graded.
 *
 * The same six values the customer portal reads from its own summary routes, produced by the same
 * `MiniGradeTally` and `MiniRiskBand`. A result must not read as low risk in HQ and medium in the
 * portal, which is what a second implementation of the arithmetic would eventually cause.
 */
export interface AdminMiniResultSummary {
    pass: number;
    fail: number;
    catastrophe: number;
    /** The share of graded tests that passed, as a whole percent. */
    health: number;
    totalGraded: number;
    riskBand: MiniRiskBand;
}

export function useAdminMiniResultSummary(
    systemCode: string,
    runId: number | string | undefined,
    resultId: number | string | undefined
): { summary: AdminMiniResultSummary | null, loading: boolean, error: string } {
    const [summary, setSummary] = useState<AdminMiniResultSummary | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect((): (() => void) => {
        if (runId === undefined || resultId === undefined) {
            setSummary(null);

            return (): void => {};
        }

        let cancelled: boolean = false;

        async function load(): Promise<void> {
            setLoading(true);
            setError('');

            const url: string = UseUrlManagerCleanUrl({ url: hostUrl + 'hq/mini/admin/result/summary' });
            const token: string = read_cookie('auth');

            try {
                const response: Response = await fetch(new Request(url, {
                    method: 'POST',
                    body: JSON.stringify({ tool: systemCode, runId: runId, resultId: resultId }),
                    headers: new Headers({
                        'Content-Type': 'application/json',
                        'token': token,
                        'hq': 'hq',
                    }),
                }));

                // 403 carries an access denied page rather than JSON, and parsing it produces
                // "unexpected token <", which says nothing about what is wrong.
                if (response.status === 403) {
                    if (!cancelled) {
                        setError('Your role does not include reading Mini grades.');
                    }

                    return;
                }

                const body = await response.json();
                if (cancelled) {
                    return;
                }
                if (body?.status !== 'success') {
                    setError(String(body?.message ?? 'That result could not be summarised.'));
                    setSummary(null);

                    return;
                }

                setSummary(body.data ?? null);
            } catch (caught: unknown) {
                if (!cancelled) {
                    setError(caught instanceof Error ? caught.message : 'That result could not be summarised.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        void load();

        // A modal can be closed and another result opened before the first answer lands, and
        // without this the first would overwrite the second's figures.
        return (): void => {
            cancelled = true;
        };
    }, [systemCode, runId, resultId]);

    return { summary, loading, error };
}
